import { useState, useMemo } from 'react';
import Footer from '@/components/feature/Footer';
import CampaignCard from '@/components/feature/CampaignCard';
import { campaigns, categories } from '@/mocks/campaigns';

const sortOptions = [
  { value: 'newest', label: 'الأحدث' },
  { value: 'most_funded', label: 'الأكثر تمويلاً' },
  { value: 'ending_soon', label: 'ينتهي قريباً' },
  { value: 'most_donors', label: 'الأكثر داعمين' },
];

const typeOptions = [
  { value: 'all', label: 'الكل' },
  { value: 'personal_support', label: 'دعم شخصي' },
];

const ExplorePage = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeType, setActiveType] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [searchQuery, setSearchQuery] = useState('');
  const [showVerifiedOnly, setShowVerifiedOnly] = useState(false);
  const [showFilterSheet, setShowFilterSheet] = useState(false);

  const filtered = useMemo(() => {
    let result = [...campaigns];

    if (searchQuery.trim()) {
      result = result.filter(
        (c) =>
          c.title.includes(searchQuery) ||
          c.description.includes(searchQuery) ||
          c.category.includes(searchQuery)
      );
    }

    if (activeCategory !== 'all') {
      const catMap: Record<string, string> = {
        education: 'تعليم',
        projects: 'مشاريع',
        creative: 'إبداع',
        arts: 'فنون',
        agriculture: 'زراعة',
        tech: 'تقنية',
        food: 'طعام',
      };
      result = result.filter((c) => c.category === catMap[activeCategory]);
    }

    if (activeType !== 'all') {
      result = result.filter((c) => c.type === activeType);
    }

    if (showVerifiedOnly) {
      result = result.filter((c) => c.isVerified);
    }

    switch (sortBy) {
      case 'most_funded':
        result.sort((a, b) => b.raisedAmount - a.raisedAmount);
        break;
      case 'ending_soon':
        result.sort((a, b) => a.daysLeft - b.daysLeft);
        break;
      case 'most_donors':
        result.sort((a, b) => b.donorsCount - a.donorsCount);
        break;
      default:
        break;
    }

    return result;
  }, [activeCategory, activeType, sortBy, searchQuery, showVerifiedOnly]);

  const activeFiltersCount = [
    activeCategory !== 'all',
    activeType !== 'all',
    showVerifiedOnly,
    sortBy !== 'newest',
  ].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-stone-50" dir="rtl">

      {/* Page Header */}
      <div className="bg-white border-b border-stone-100 pt-24 pb-6">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <h1 className="text-2xl md:text-4xl font-black text-stone-800 mb-1">استكشف الحملات</h1>
          <p className="text-stone-500 text-sm">اكتشف حملات تستحق دعمك من مبدعين وشباب موهوب</p>

          {/* Search Bar */}
          <div className="mt-4 relative max-w-xl">
            <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
              <i className="ri-search-line text-stone-400 text-lg"></i>
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="ابحث عن حملة، مشروع، أو قضية..."
              className="w-full bg-stone-50 border border-stone-200 rounded-2xl pr-12 pl-4 py-3 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:border-emerald-400 focus:bg-white transition-all"
            />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
        <div className="flex flex-col lg:flex-row gap-8">

          {/* ── Desktop Sidebar ── */}
          <aside className="hidden lg:block w-64 shrink-0">
            <div className="bg-white rounded-2xl border border-stone-100 p-5 sticky top-24">
              <h3 className="font-bold text-stone-800 mb-4 text-sm">تصفية النتائج</h3>

              <div className="mb-5">
                <p className="text-xs text-stone-500 font-semibold mb-2 uppercase tracking-wide">نوع الحملة</p>
                <div className="flex flex-col gap-2">
                  {typeOptions.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setActiveType(opt.value)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm transition-colors cursor-pointer text-right whitespace-nowrap ${
                        activeType === opt.value
                          ? 'bg-emerald-50 text-emerald-700 font-semibold'
                          : 'text-stone-600 hover:bg-stone-50'
                      }`}
                    >
                      <span className={`w-2 h-2 rounded-full ${activeType === opt.value ? 'bg-emerald-500' : 'bg-stone-200'}`}></span>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-5 pb-5 border-b border-stone-100">
                <button
                  onClick={() => setShowVerifiedOnly(!showVerifiedOnly)}
                  className="flex items-center justify-between w-full cursor-pointer"
                >
                  <span className="text-sm text-stone-700">الموثقة فقط</span>
                  <div className={`w-10 h-5 rounded-full transition-colors relative ${showVerifiedOnly ? 'bg-emerald-500' : 'bg-stone-200'}`}>
                    <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${showVerifiedOnly ? 'left-5' : 'left-0.5'}`}></div>
                  </div>
                </button>
              </div>

              <div>
                <p className="text-xs text-stone-500 font-semibold mb-2 uppercase tracking-wide">ترتيب حسب</p>
                <div className="flex flex-col gap-1">
                  {sortOptions.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setSortBy(opt.value)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm transition-colors cursor-pointer text-right whitespace-nowrap ${
                        sortBy === opt.value
                          ? 'bg-emerald-50 text-emerald-700 font-semibold'
                          : 'text-stone-600 hover:bg-stone-50'
                      }`}
                    >
                      <i className={`ri-check-line text-xs ${sortBy === opt.value ? 'text-emerald-500' : 'text-transparent'}`}></i>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          {/* ── Main Content ── */}
          <div className="flex-1 min-w-0">

            {/* ── Mobile: Filter Bar ── */}
            <div className="flex lg:hidden items-center flex-wrap gap-1.5 mb-4">
              {/* Filter Button */}
              <button
                onClick={() => setShowFilterSheet(true)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-white border border-stone-200 text-stone-700 text-xs font-medium cursor-pointer relative"
              >
                <i className="ri-equalizer-2-line text-xs"></i>
                فلترة
                {activeFiltersCount > 0 && (
                  <span className="absolute -top-1 -left-1 w-4 h-4 flex items-center justify-center bg-emerald-600 text-white text-[10px] font-bold rounded-full">
                    {activeFiltersCount}
                  </span>
                )}
              </button>

              {/* Sort Chips */}
              {sortOptions.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setSortBy(opt.value)}
                  className={`px-2.5 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-all ${
                    sortBy === opt.value
                      ? 'bg-emerald-600 text-white'
                      : 'bg-white text-stone-600 border border-stone-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {/* Category Tabs */}
            <div className="flex flex-wrap gap-1.5 mb-5 lg:mx-0 lg:px-0">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer ${
                    activeCategory === cat.id
                      ? 'bg-emerald-600 text-white'
                      : 'bg-white text-stone-600 border border-stone-200 hover:border-emerald-300'
                  }`}
                >
                  <i className={`${cat.icon} text-xs`}></i>
                  {cat.label}
                </button>
              ))}
            </div>

            {/* Results Count */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-stone-500 text-sm">
                <span className="font-bold text-stone-800">{filtered.length}</span> حملة
              </p>
              {activeFiltersCount > 0 && (
                <button
                  onClick={() => { setActiveCategory('all'); setActiveType('all'); setSortBy('newest'); setShowVerifiedOnly(false); }}
                  className="text-xs text-emerald-600 font-semibold cursor-pointer whitespace-nowrap hover:underline"
                >
                  مسح الفلاتر
                </button>
              )}
            </div>

            {/* Grid */}
            {filtered.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
                {filtered.map((campaign, i) => (
                  <CampaignCard key={campaign.id} campaign={campaign} index={i} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center bg-stone-100 rounded-2xl mx-auto mb-4">
                  <i className="ri-search-line text-2xl text-stone-400"></i>
                </div>
                <h3 className="font-bold text-stone-700 mb-2">لا توجد نتائج</h3>
                <p className="text-stone-400 text-sm">جرب تغيير معايير البحث أو التصفية</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Mobile Filter Bottom Sheet ── */}
      {showFilterSheet && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/40 z-40 lg:hidden"
            onClick={() => setShowFilterSheet(false)}
          ></div>

          {/* Sheet */}
          <div className="fixed bottom-0 right-0 left-0 z-50 lg:hidden bg-white rounded-t-3xl px-5 pt-4 pb-8 animate-slide-up"
            style={{ animation: 'slideUp 0.3s ease' }}
          >
            {/* Handle */}
            <div className="w-10 h-1 bg-stone-200 rounded-full mx-auto mb-5"></div>

            <div className="flex items-center justify-between mb-5">
              <h3 className="font-black text-stone-800 text-base">تصفية النتائج</h3>
              <button
                onClick={() => { setActiveCategory('all'); setActiveType('all'); setSortBy('newest'); setShowVerifiedOnly(false); }}
                className="text-xs text-emerald-600 font-semibold cursor-pointer whitespace-nowrap"
              >
                مسح الكل
              </button>
            </div>

            {/* Type */}
            <div className="mb-5">
              <p className="text-xs text-stone-400 font-semibold mb-3 uppercase tracking-wide">نوع الحملة</p>
              <div className="flex gap-2 flex-wrap">
                {typeOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setActiveType(opt.value)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                      activeType === opt.value
                        ? 'bg-emerald-600 text-white'
                        : 'bg-stone-100 text-stone-600'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Verified */}
            <div className="mb-5 pb-5 border-b border-stone-100">
              <button
                onClick={() => setShowVerifiedOnly(!showVerifiedOnly)}
                className="flex items-center justify-between w-full cursor-pointer"
              >
                <div>
                  <p className="text-sm font-semibold text-stone-800">الموثقة فقط</p>
                  <p className="text-xs text-stone-400">عرض الحملات الموثقة فقط</p>
                </div>
                <div className={`w-12 h-6 rounded-full transition-colors relative shrink-0 ${showVerifiedOnly ? 'bg-emerald-500' : 'bg-stone-200'}`}>
                  <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${showVerifiedOnly ? 'left-7' : 'left-1'}`}></div>
                </div>
              </button>
            </div>

            {/* Sort */}
            <div className="mb-6">
              <p className="text-xs text-stone-400 font-semibold mb-3 uppercase tracking-wide">ترتيب حسب</p>
              <div className="flex gap-2 flex-wrap">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSortBy(opt.value)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                      sortBy === opt.value
                        ? 'bg-emerald-600 text-white'
                        : 'bg-stone-100 text-stone-600'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={() => setShowFilterSheet(false)}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3.5 rounded-2xl transition-colors cursor-pointer whitespace-nowrap"
            >
              عرض {filtered.length} نتيجة
            </button>
          </div>
        </>
      )}

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>

      <Footer />
    </div>
  );
};

export default ExplorePage;
